/**
 * Contrato de um renderer de nó.
 *
 * Todo componente que desenha um nó da tela implementa isto, e recebe sempre os
 * mesmos três dados. Não há um quarto: se um renderer precisar de mais alguma
 * coisa, ela vem por injeção (serviço), não por entrada — senão o `NodeHost`
 * teria de saber o que cada tipo precisa, que é justamente o `ngSwitch` de
 * quatro níveis que estamos matando.
 */
import { InjectionToken, InputSignal, Type } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { NodeType, ScreenNode } from '../model/node-props';

/**
 * Como a tela está sendo desenhada.
 *
 * `view`         — produção. Sem overlay, sem seleção, sem nada de edição.
 * `editPreview`  — dentro do editor. O renderer é O MESMO; o que muda é que o
 *                  `EDITOR_CHROME` está provido, e aí o `NodeHost` decora. Ver
 *                  `editor-chrome.ts`.
 */
export type RenderMode = 'view' | 'editPreview';

/**
 * As três entradas, como VALOR — é este objeto que o `ngComponentOutlet` recebe.
 */
export interface NodeRendererInputs {
  /** O nó a desenhar. */
  readonly node: ScreenNode;
  /** O formulário da tela inteira. Nulo em tela sem campo nenhum. */
  readonly form: FormGroup | null;
  readonly mode: RenderMode;
}

/**
 * O contrato do COMPONENTE, com as entradas como signal.
 *
 * Separado de `NodeRendererInputs` porque são coisas diferentes: aqui é o que a
 * classe expõe (`InputSignal`), lá é o que o outlet passa (valores). Um
 * `implements NodeRenderer<'panel'>` no renderer garante, em tempo de
 * compilação, que ninguém renomeie uma entrada e quebre o despachante em runtime.
 */
export interface NodeRenderer<K extends NodeType = NodeType> {
  readonly node: InputSignal<ScreenNode<K>>;
  readonly form: InputSignal<FormGroup | null>;
  readonly mode: InputSignal<RenderMode>;
}

/**
 * Mapa tipo → componente que o desenha.
 *
 * Fica AQUI, e não no `ComponentDescriptor`, de propósito.
 *
 * O plano original punha `render: Type<NodeRenderer<K>>` dentro do descriptor.
 * Na Fase 2 ficou claro o custo disso: o descriptor passaria a importar
 * `@angular/core`, e com ele o registry inteiro — que hoje é TypeScript puro,
 * testável sem TestBed, e onde moram as regras de aninhamento e o esquema do
 * inspector. Separar mantém `model → registry` sem uma linha de Angular, e o
 * custo de estender continua o mesmo que o plano prometeu: um descriptor e um
 * renderer, dois toques.
 *
 * A garantia de completude não se perde: `renderer-map.spec.ts` afirma que todo
 * `NodeType` tem renderer, então esquecer um quebra o teste, não a tela.
 */
export type NodeRendererMap = Readonly<Record<NodeType, Type<NodeRenderer>>>;

/**
 * Renderers em uso. Provido pelo módulo de render; o editor pode sobrepor um
 * tipo para experimentar um widget novo sem tocar no mapa nativo.
 */
export const NODE_RENDERERS = new InjectionToken<NodeRendererMap>('NODE_RENDERERS');
